import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';

/**
 * Typing anywhere on a screen types into its search box (SCOPE §2-1).
 *
 * **A USB scanner is a keyboard**, and a keyboard sends its characters wherever
 * the focus happens to be. Focus does not stay in the search box on its own: a
 * dialog closes, a button is pressed, a table row is clicked, and the next scan
 * goes nowhere at all. Nothing appears on screen, so the operator scans again,
 * harder, and concludes the scanner is broken — which is the most expensive
 * possible reading of "we forgot to restore focus".
 *
 * Restoring focus on every path that could lose it is a list nobody finishes.
 * This is the backstop that does not depend on remembering: **a printable
 * character with nowhere sensible to go goes to the search box.** The operator
 * never clicks into it, and a scan works from wherever they were.
 *
 * What it deliberately does not do:
 *
 *  - **Never steal from another input.** If the caret is in a field, a
 *    textarea, a select or anything `contenteditable`, that is where the
 *    characters belong. Stealing here would make every other form on the screen
 *    unusable — the failure this is most likely to cause, so it is checked
 *    first.
 *  - **Never fire while a modal is open.** A `<dialog>` in the top layer is a
 *    question being asked; a scan behind it belongs to the screen underneath,
 *    which is not the one the operator is looking at.
 *  - **Never reach into a shadow tree.** The feedback hub's widget renders its
 *    whole form inside a shadow root, and the DOM tells this handler two lies
 *    about it: the keystroke's `target` is retargeted to the host `<div>` (so
 *    the is-it-an-input check sees a DIV), and `querySelector('dialog[open]')`
 *    cannot see an open modal that lives behind a shadow boundary. Both lies
 *    landed every character typed into the feedback form in the register's
 *    search box instead. `composedPath()[0]` names the element really typed
 *    into, a retargeted event is foreign territory by definition, and the
 *    modal check asks the known shadow hosts directly.
 *  - **Never swallow a shortcut.** Ctrl, Meta and Alt combinations are somebody
 *    trying to copy, paste or switch tab.
 *  - **Only printable characters.** Enter, Tab, arrows and Escape all mean
 *    something already, and a scanner's trailing Enter must reach the box it
 *    just filled rather than being intercepted here.
 *
 * The keystroke is applied by hand rather than left to land on the newly
 * focused element, because whether it does is browser-dependent — and a scanner
 * that loses its first character is worse than one that loses all of them,
 * since the wrong item is found rather than none.
 */

/** Everything the decision needs, read off the DOM by the hook. */
export interface TypingSignals {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly defaultPrevented: boolean;
  /** The search box already holds the caret — nothing to do. */
  readonly searchHasFocus: boolean;
  /**
   * The search box is on screen. The register stays mounted, hidden, behind
   * every other section — and its listener took a scan made on Stock into its
   * own invisible box, so the scan vanished (FB-1115). A hidden box takes
   * nothing.
   */
  readonly searchVisible: boolean;
  /** A modal is asking a question, in the light DOM or behind a shadow root. */
  readonly modalOpen: boolean;
  /**
   * `composedPath()[0]` differs from `event.target`: the keystroke came from
   * inside a shadow tree and was retargeted crossing the boundary.
   */
  readonly crossedShadowBoundary: boolean;
  /** The element the keystroke really landed on, after unretargeting. */
  readonly target: { readonly tag: string; readonly editable: boolean } | null;
}

export function shouldCaptureTyping(signals: TypingSignals): boolean {
  if (signals.ctrlKey || signals.metaKey || signals.altKey) return false;
  // A printable character is exactly one code point. `Shift`, `Enter`,
  // `ArrowLeft` and friends are all longer.
  if (signals.key.length !== 1) return false;
  if (signals.defaultPrevented) return false;
  if (signals.searchHasFocus) return false;
  if (!signals.searchVisible) return false;
  if (signals.modalOpen) return false;
  if (signals.crossedShadowBoundary) return false;
  if (signals.target !== null) {
    if (signals.target.editable) return false;
    const tag = signals.target.tag;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  }
  return true;
}

/**
 * Hosts whose shadow trees can hold an open modal. `querySelector` stops at a
 * shadow boundary, so each one is asked by hand; a new third-party element
 * that opens an overlay joins this list or its modal is invisible here.
 */
const SHADOW_MODAL_HOSTS = '.refract-feedback';

function modalIsOpen(): boolean {
  if (document.querySelector('dialog[open], [aria-modal="true"]') !== null) return true;
  for (const host of document.querySelectorAll(SHADOW_MODAL_HOSTS)) {
    const root = host.shadowRoot;
    if (root !== null && root.querySelector('dialog[open], [aria-modal="true"]') !== null) {
      return true;
    }
  }
  return false;
}

/**
 * A screen's one search box, taking typing from anywhere on the screen
 * (client, 2026-10-15: "when i type it doesn't type into search bar
 * automatically, it should do that on every screen with 1 search bar").
 *
 * Returns the ref to put on the box. Every screen with a single search uses
 * this, so the rule is written once; `e2e/tests/typing-focus.spec.ts` presses
 * a key on each of them.
 */
export function useSearchTyping(
  setTerm: Dispatch<SetStateAction<string>>,
  enabled = true,
): RefObject<HTMLInputElement | null> {
  const ref = useRef<HTMLInputElement>(null);
  const append = useCallback(
    (character: string) => setTerm((current) => current + character),
    [setTerm],
  );
  useTypingFocus(ref, append, enabled);
  return ref;
}

export function useTypingFocus(
  inputRef: RefObject<HTMLInputElement | null>,
  onCharacter: (character: string) => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;

    const handler = (event: KeyboardEvent): void => {
      const input = inputRef.current;
      if (input === null) return;

      const landed = event.composedPath()[0];
      const element = landed instanceof HTMLElement ? landed : null;

      const capture = shouldCaptureTyping({
        key: event.key,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
        defaultPrevented: event.defaultPrevented,
        searchHasFocus: document.activeElement === input,
        searchVisible: input.getClientRects().length > 0,
        modalOpen: modalIsOpen(),
        crossedShadowBoundary: landed !== event.target,
        target:
          element === null ? null : { tag: element.tagName, editable: element.isContentEditable },
      });
      if (!capture) return;

      event.preventDefault();
      input.focus();
      onCharacter(event.key);
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [inputRef, onCharacter, enabled]);
}
